import {
  Activity,
  CalendarDays,
  CircleDollarSign,
  Clock,
  Footprints,
  Hash,
  Heart,
  Mail,
  Network,
  PenLine,
  Phone,
  Sigma,
  Smile,
  Sparkles,
  UserRound,
} from "lucide-react";

export type CustomerAttributeSource = "given" | "computed" | "ai";
export type BuiltInCustomerAttributeKey =
  | "name"
  | "status"
  | "channel"
  | "interest"
  | "bestTime"
  | "sentiment"
  | "nextStep"
  | "email"
  | "phone"
  | "bookings"
  | "orderValue"
  | "joined"
  | "activity";
export type CustomerAttributeKey = BuiltInCustomerAttributeKey | `custom:${string}`;

export interface CustomAttributeDefinition {
  id: string;
  name: string;
  source: CustomerAttributeSource;
  logic: string;
}

export interface CustomerAttributeDefinition {
  key: CustomerAttributeKey;
  label: string;
  source: CustomerAttributeSource;
  width: number;
  icon: typeof UserRound;
}

export const CUSTOMER_ATTRIBUTES: readonly CustomerAttributeDefinition[] = [
  { key: "name", label: "Name", source: "given", width: 250, icon: UserRound },
  { key: "status", label: "Status", source: "computed", width: 140, icon: Activity },
  { key: "channel", label: "Preferred channel", source: "computed", width: 180, icon: Network },
  // Filled in from what the Customer Graph already stores; see customerInsights.ts.
  { key: "interest", label: "Interested in", source: "ai", width: 240, icon: Heart },
  { key: "bestTime", label: "Best time", source: "ai", width: 170, icon: Clock },
  { key: "sentiment", label: "Sentiment", source: "ai", width: 140, icon: Smile },
  { key: "nextStep", label: "Next best step", source: "ai", width: 260, icon: Footprints },
  { key: "email", label: "Email", source: "given", width: 300, icon: Mail },
  { key: "phone", label: "Phone number", source: "given", width: 210, icon: Phone },
  { key: "bookings", label: "Bookings", source: "computed", width: 130, icon: Hash },
  { key: "orderValue", label: "Total order value", source: "computed", width: 190, icon: CircleDollarSign },
  { key: "joined", label: "Date joined", source: "given", width: 180, icon: CalendarDays },
  { key: "activity", label: "Last activity", source: "computed", width: 180, icon: Activity },
] as const;

export const ATTRIBUTE_SOURCE_LABELS: Record<CustomerAttributeSource, string> = {
  given: "Given",
  computed: "Computed",
  ai: "Inferred",
};

export const ATTRIBUTE_SOURCE_ICONS: Record<CustomerAttributeSource, typeof PenLine> = {
  given: PenLine,
  computed: Sigma,
  ai: Sparkles,
};

export const HIDDEN_ATTRIBUTES_STORAGE_KEY = "dibs.customer-hidden-attributes:v1";
export const CUSTOM_ATTRIBUTES_STORAGE_KEY = "dibs.customer-custom-attributes:v1";

export const loadCustomAttributes = (): CustomAttributeDefinition[] => {
  if (typeof window === "undefined") return [];
  try {
    const stored = JSON.parse(window.localStorage.getItem(CUSTOM_ATTRIBUTES_STORAGE_KEY) ?? "[]");
    return Array.isArray(stored)
      ? stored.filter((attribute): attribute is CustomAttributeDefinition =>
          typeof attribute?.id === "string"
          && typeof attribute?.name === "string"
          && typeof attribute?.logic === "string"
          && ["given", "computed", "ai"].includes(attribute?.source))
      : [];
  } catch {
    return [];
  }
};

export const loadHiddenAttributes = () => {
  if (typeof window === "undefined") return new Set<CustomerAttributeKey>();
  try {
    const stored = JSON.parse(window.localStorage.getItem(HIDDEN_ATTRIBUTES_STORAGE_KEY) ?? "[]");
    return new Set<CustomerAttributeKey>(
      Array.isArray(stored)
        ? stored.filter((key): key is CustomerAttributeKey =>
            typeof key === "string"
            && key !== "name"
            && (CUSTOMER_ATTRIBUTES.some((attribute) => attribute.key === key) || key.startsWith("custom:")))
        : [],
    );
  } catch {
    return new Set<CustomerAttributeKey>();
  }
};
